import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { SynchronousQueryConfig } from 'lib/react-query';
import { queryClient } from 'lib/react-query';
import { apiClient } from 'lib/api';
import { isAxiosError } from 'axios';
import queryString from 'query-string';
import queryKeys from './queryKeys';
import type { RowStatus } from './getCampaigns';
import { mergeCachedAdSetFlowsFromClient } from '../utils/adSetFlowCache';

export interface AdSetEntry {
  id: string;
  name: string;
  ads: number;
  status: string;
  account_id: string;
  campaign_id: string;
  campaign_status?: RowStatus;
  disabled?: boolean;
  budget: number;
  duplicates_count?: number;
  is_cbo: boolean;
  start_time?: string;
  /** May hold multiple comma-separated flow names (e.g. "flowA,flowB") when user-assigned in the grid. */
  flow?: string | null;
  /** Client-only: true when flow was chosen via SelectFlows on a template ad set. */
  flowAssignedByUser?: boolean;
}

export {
  isAdSetFlowEmpty,
  updateAdSetFlowInCache,
} from '../utils/adSetFlowCache';

interface GetAdSetsParams {
  selectedCampaigns: string[];
  platform: string;
  query?: string[];
}

export const getAdSets = async (
  { params }: { params: GetAdSetsParams },
  signal?: AbortSignal,
): Promise<AdSetEntry[]> => {
  const { selectedCampaigns, platform, query } = params;

  const promises = selectedCampaigns.map((id) =>
    apiClient.get<AdSetEntry[]>(`/api/v1/facebook_manager/get_adsets/${id}`, {
      signal,
      params: { platform, query },
      paramsSerializer: (prms) =>
        queryString.stringify(prms, { arrayFormat: 'comma' }),
    }),
  );

  try {
    const responses = await Promise.all(promises);

    const isError = responses.some((r) => isAxiosError(r));
    if (isError) {
      const errorResponses = responses.filter((response, index) => {
        const error = isAxiosError(response);
        if (error) {
          response.message = `An error occurred while fetching AdSet for campaign ${selectedCampaigns[index]}`;
        }
        return error;
      });

      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject(errorResponses);
    }

    const successfulResponses = responses.filter(
      (response) => response?.status >= 200 && response?.status <= 400,
    );
    return successfulResponses.flatMap((response) => response.data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

interface UseAdSetsOptions {
  params: GetAdSetsParams;
  config?: SynchronousQueryConfig<typeof getAdSets, AdSetEntry[]>;
}

export const useAdSets = ({ params, config }: UseAdSetsOptions) => {
  const queryKey = [
    ...queryKeys.adSets,
    params.query,
    params.selectedCampaigns,
    params.platform,
  ] as QueryKey;

  return useQuery({
    staleTime: 0,
    ...config,
    retryOnMount: true,
    queryFn: async ({ signal }) => {
      const fresh = await getAdSets({ params }, signal);
      return mergeCachedAdSetFlowsFromClient(queryClient, fresh, queryKey);
    },
    queryKey,
  });
};
